import { okSchema } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { Menu, VenetianMask, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PreviewBanner } from '@/components/customisation/preview-banner';
import { SiteFooter } from '@/components/customisation/site-footer';
import { Breadcrumbs } from '@/components/shell/breadcrumbs';
import { AccountMenu, Logo, Sidebar, SidebarNav, UserAvatar } from '@/components/shell/sidebar';
import { ThemeMenu } from '@/components/theme-menu';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/api';
import { setDocumentTitle } from '@/lib/customisation/apply';
import { displayName } from '@/lib/customisation/logos';
import { useSite } from '@/lib/customisation/site';
import { errorMessage } from '@/lib/forms';
import { sessionQuery, switchIdentity, useAuthenticatedSession } from '@/lib/session';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session.authenticated) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href === '/' ? undefined : location.href },
      });
    }
  },
  component: AppLayout,
});

const SIDEBAR_KEY = 'hearth.sidebar';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function AppLayout() {
  const session = useAuthenticatedSession();
  const site = useSite();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Custom CSS is scoped to .hearth-custom, which admin pages never carry.
  const adminPage = pathname === '/admin' || pathname.startsWith('/admin/');
  // Hearth AI fills the window below the header (its own scroll areas); a footer under it
  // made the page taller than the window, so typing scrolled the conversation off the top.
  const fullHeightPage = pathname === '/ai' || pathname.startsWith('/ai/');
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, c ? 'expanded' : 'collapsed');
      } catch {
        // Not remembered, but still applies for this visit.
      }
      return !c;
    });

  useEffect(() => {
    setDocumentTitle(displayName(site, session.panel.name));
  }, [site, session.panel.name]);

  return (
    <div className={cn('flex min-h-dvh', !adminPage && 'hearth-custom')}>
      <a
        href="#main"
        className="bg-primary text-primary-foreground sr-only z-50 rounded-md px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div className="flex min-w-0 flex-1 flex-col">
        <PreviewBanner />
        {session.impersonator && (
          <ImpersonationBanner
            username={session.user.username}
            admin={session.impersonator.username}
          />
        )}
        <Header />
        {!session.user.emailVerified && !session.impersonator && (
          <VerifyEmailBanner email={session.user.email} />
        )}
        <main
          id="main"
          tabIndex={-1}
          className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 outline-none sm:px-6 sm:py-8 lg:px-8"
        >
          <Outlet />
        </main>
        {!adminPage && !fullHeightPage && <SiteFooter site={site} />}
      </div>
    </div>
  );
}

function Header() {
  const session = useAuthenticatedSession();
  const [drawer, setDrawer] = useState(false);
  const loading = useRouterState({ select: (s) => s.status === 'pending' });
  return (
    <header className="bg-background/80 sticky top-0 z-40 border-b backdrop-blur-md">
      {loading && (
        <div
          role="progressbar"
          aria-label="Loading page"
          className="pointer-events-none absolute inset-x-0 -bottom-px h-0.5 overflow-hidden"
        >
          <div className="route-progress bg-brand h-full w-2/5 rounded-full" />
        </div>
      )}
      <div className="flex h-14 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <Dialog.Root open={drawer} onOpenChange={setDrawer}>
          <Dialog.Trigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1 lg:hidden"
              aria-label="Open navigation"
            >
              <Menu />
            </Button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 lg:hidden" />
            <Dialog.Content className="bg-sidebar data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r shadow-2xl duration-200 outline-none lg:hidden">
              <Dialog.Title className="sr-only">Navigation</Dialog.Title>
              <Dialog.Description className="sr-only">Sections of the panel</Dialog.Description>
              <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
                <Logo name={session.panel.name} />
                <Dialog.Close asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Close navigation">
                    <X />
                  </Button>
                </Dialog.Close>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">
                <SidebarNav onNavigate={() => setDrawer(false)} />
              </div>
              <DrawerAccount onNavigate={() => setDrawer(false)} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <span className="lg:hidden">
          <Logo name={session.panel.name} compact place="header" />
        </span>
        <Breadcrumbs />
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <ThemeMenu />
          <AccountMenu>
            <Button variant="ghost" size="icon" className="rounded-md" aria-label="Account menu">
              <UserAvatar username={session.user.username} className="size-7" />
            </Button>
          </AccountMenu>
        </div>
      </div>
    </header>
  );
}

/** The signed-in user at the foot of the mobile drawer, like the desktop sidebar's. */
function DrawerAccount({ onNavigate }: { onNavigate: () => void }) {
  const { user, impersonator } = useAuthenticatedSession();
  return (
    <div className="flex items-center gap-2.5 border-t p-3">
      <UserAvatar username={user.username} />
      <span className="grid min-w-0 flex-1 leading-tight">
        <span className="truncate text-sm font-medium">{user.username}</span>
        <span className="text-muted-foreground truncate text-xs">
          {impersonator ? `Impersonated by ${impersonator.username}` : user.email}
        </span>
      </span>
      <Button asChild variant="ghost" size="sm" onClick={onNavigate}>
        <Link to="/account">Account</Link>
      </Button>
    </div>
  );
}

function ImpersonationBanner({ username, admin }: { username: string; admin: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const stop = useMutation({
    mutationFn: () =>
      apiRequest('/api/auth/impersonation/stop', { method: 'POST', schema: okSchema }),
    onSuccess: async () => {
      await switchIdentity(queryClient);
      await navigate({ to: '/admin/users', search: {} });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="bg-warning/15 border-warning/30 border-b">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm sm:px-6 lg:px-8">
        <VenetianMask className="text-warning size-4" />
        <span>
          You ({admin}) are signed in as <strong>{username}</strong>. Actions are logged against
          both accounts.
        </span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7"
          onClick={() => stop.mutate()}
          disabled={stop.isPending}
        >
          Stop impersonating
        </Button>
      </div>
    </div>
  );
}

function VerifyEmailBanner({ email }: { email: string }) {
  const resend = useMutation({
    mutationFn: () => apiRequest('/api/auth/email/resend', { method: 'POST', schema: okSchema }),
    onSuccess: () => toast.success(`Verification email sent to ${email}.`),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="border-b">
      <div className="text-muted-foreground flex flex-wrap items-center gap-3 px-4 py-2 text-sm sm:px-6 lg:px-8">
        <span>Please verify your email address ({email}).</span>
        <Button
          size="sm"
          variant="link"
          className="h-auto p-0"
          onClick={() => resend.mutate()}
          disabled={resend.isPending || resend.isSuccess}
        >
          {resend.isSuccess ? 'Sent' : 'Resend verification email'}
        </Button>
      </div>
    </div>
  );
}
